import firebase from "firebase/app";
import "firebase/functions";
import firestore from "src/apiService/firestore";
import storage from "src/apiService/storage";
import { getUserId } from "src/config/storage";
import MonthYear from "src/interfaces/monthYear.interface";

export async function fetchNewEbayListingsApi(integrationId) {
  const getInventoriesFunction = firebase
    .functions()
    .httpsCallable("getNeweBayInventory");

  const result = await getInventoriesFunction({ integrationId });
  return result.data;
}

export async function fetchNewEbayTransactionsApi(
  integrationId: string,
  start: MonthYear,
  end: MonthYear
) {
  const getTransactionsFunction = firebase
    .functions()
    .httpsCallable("getNeweBayTransactions");

  const result = await getTransactionsFunction({
    integrationId,
    year: start.year,
    month: start.month,
    end,
    timezoneOffset: new Date().getTimezoneOffset(),
  });
  return result.data;
}

export async function ebaySyncApi(integrationId: string) {
  const f = firebase.functions().httpsCallable("ebaySync");
  const result = await f({ integrationId });
  return result.data;
}

export async function eBayUpdateItemTitles(file) {
  const userId = await getUserId();
  const storageRef = storage().ref();
  const filename: string = `ebayItemTitles/${userId}/${Date.now()}`;
  const uploadRef = storageRef.child(filename);
  const snapshot = await uploadRef.put(file);

  if (snapshot.state !== firebase.storage.TaskState.SUCCESS)
    throw new Error(
      "There was an error uploading the file. Please try again later"
    );

  const f = firebase.functions().httpsCallable("eBayUpdateItemTitles");
  await f({ filename });
}

export async function eBayIntegrationDelete(id: string): Promise<void> {
  const f = firebase.functions().httpsCallable("eBayDeleteIntegration");
  await f({ integrationId: id });
}

export async function eBayIntegrationSyncChange(
  id: string,
  sync: boolean,
  inventory: boolean = true
) {
  const userId = await getUserId();
  const db = firestore();

  if (id === "ebay") {
    await db
      .collection("Users")
      .doc(userId)
      .update({
        ebayIntegration: sync,
        ebayIntegrationInventory: sync && inventory,
      });
    return;
  }

  await db.runTransaction(async (transaction) => {
    const ref = db.collection("Plaid_Integrations").doc(id);
    const doc = await transaction.get(ref);
    if (!doc.exists) return;
    if (doc.data()?.user !== userId) return;
    await transaction.update(ref, { sync, inventory: sync && inventory });
  });
}

export async function getEBayOAuthUrl(
  arg: (
    | { integrationId: string }
    | { integrationName: string; bankAccountId?: string | undefined }
    | { integrationName?: string; bankAccountId: string }
    | { integrationId: "ebay"; integrationName: string }
  ) & { back?: string }
) {
  const f = firebase.functions().httpsCallable("getEBayOAuthUrl");
  return await f(arg);
}

export async function exchangeEBayOAuthCode({
  code,
  integrationId,
  integrationName,
  bankAccountId,
}: {
  code: string;
  integrationId?: string;
  integrationName?: string;
  bankAccountId?: string;
}) {
  const f = firebase.functions().httpsCallable("exchangeEBayOAuthCode");
  return await f({ code, integrationId, integrationName, bankAccountId });
}

export async function consolidateOrders({ orderIds }: { orderIds: string[] }) {
  const f = firebase.functions().httpsCallable("consolidateOrders");

  return await f({ orderIds });
}

export async function getLatestEbayIntegration() {
  const uid = await getUserId();
  const db = firestore();
  const integrations = await db
    .collection("Plaid_Integrations")
    .where("user", "==", uid)
    .where("type", "==", "ebay")
    .get();

  const latestIntegration = integrations.docs
    .map((doc) => doc.data())
    .filter((integration) => integration && integration.createdAt)
    .sort((a, b) => {
      const aTime = a.createdAt?.toMillis
        ? a.createdAt.toMillis()
        : new Date(a.createdAt).getTime();
      const bTime = b.createdAt?.toMillis
        ? b.createdAt.toMillis()
        : new Date(b.createdAt).getTime();
      return bTime - aTime;
    })[0];

  return latestIntegration ?? null;
}
